import { dbStorage } from './indexedDBStorage'
import {
  buildLedgerSafeWitnessStateSnapshotsFromRows,
  cloneLegacySegmentationResult,
} from './ledgerSafePersistenceAdapter'
import type { Witness } from '../store/booksStore'
import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  LedgerSafeActiveLedgerRecord,
  LedgerSafeLedgerBundle,
  LedgerSafeLedgerRecord,
  LedgerSafeLedgerSource,
  LedgerSafeLedgerStateRecord,
  LedgerSafeWitnessStateSnapshot,
} from '../types/ledgerSafeLedgers'

const nowIso = (): string => new Date().toISOString()

const generateId = (prefix: string): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const cloneRows = (rows: AnchoredRow[]): AnchoredRow[] =>
  rows.map(row => ({
    ...row,
    unitMappings: row.unitMappings.map(mapping => ({ ...mapping })),
  }))

const cloneWitnessStates = (states: LedgerSafeWitnessStateSnapshot[]): LedgerSafeWitnessStateSnapshot[] =>
  states.map(state => ({
    ...state,
    segmentationResult: cloneLegacySegmentationResult(state.segmentationResult),
  }))

const stableJson = (value: unknown): string => JSON.stringify(value, (_key, nestedValue) => {
  if (!nestedValue || typeof nestedValue !== 'object' || Array.isArray(nestedValue)) {
    return nestedValue
  }
  return Object.keys(nestedValue as Record<string, unknown>)
    .sort()
    .reduce<Record<string, unknown>>((accumulator, key) => {
      accumulator[key] = (nestedValue as Record<string, unknown>)[key]
      return accumulator
    }, {})
})

export const hashLedgerSafeValue = (value: unknown): string => {
  const input = stableJson(value)
  let hash = 0
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) - hash + input.charCodeAt(index)) | 0
  }
  return `${(hash >>> 0).toString(16)}:${input.length}`
}

export const buildLedgerSafeWitnessStateSnapshots = (
  rows: AnchoredRow[],
  witnesses: Witness[],
): LedgerSafeWitnessStateSnapshot[] => buildLedgerSafeWitnessStateSnapshotsFromRows(rows, witnesses)

export const doLedgerSafeRowsMatchState = (
  rows: AnchoredRow[],
  state: LedgerSafeLedgerStateRecord,
): boolean => hashLedgerSafeValue(rows) === hashLedgerSafeValue(state.rows)

const buildLedgerSafeStateHash = (
  rows: AnchoredRow[],
  witnessStates: LedgerSafeWitnessStateSnapshot[],
): string => hashLedgerSafeValue({
  rows,
  witnessStates,
})

export const listLedgerSafeLedgerBundles = async (
  contextId: string,
): Promise<LedgerSafeLedgerBundle[]> => {
  await dbStorage.ready()
  const ledgers = await dbStorage.getAllByIndex('ledger_safe_ledgers', 'contextId', contextId) as LedgerSafeLedgerRecord[]
  const states = await dbStorage.getAllByIndex('ledger_safe_ledger_states', 'contextId', contextId) as LedgerSafeLedgerStateRecord[]
  const statesByLedgerId = new Map<string, LedgerSafeLedgerStateRecord[]>()
  states.forEach(state => {
    const existing = statesByLedgerId.get(state.ledgerId) || []
    statesByLedgerId.set(state.ledgerId, [...existing, state])
  })

  return ledgers
    .map(ledger => {
      const ledgerStates = (statesByLedgerId.get(ledger.id) || [])
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      const baseline = ledgerStates.find(state => state.id === ledger.baselineStateId)
        || ledgerStates.find(state => state.kind === 'baseline')
        || null
      if (!baseline) {
        return null
      }
      const latestWorking = (
        ledger.latestWorkingStateId
          ? ledgerStates.find(state => state.id === ledger.latestWorkingStateId)
          : null
      ) || ledgerStates.find(state => state.kind === 'working') || null
      return {
        ledger,
        states: ledgerStates,
        baseline,
        latestWorking,
      }
    })
    .filter((bundle): bundle is LedgerSafeLedgerBundle => Boolean(bundle))
    .sort((left, right) => right.ledger.updatedAt.localeCompare(left.ledger.updatedAt))
}

export const getLedgerSafeActiveLedger = async (
  contextId: string,
): Promise<LedgerSafeActiveLedgerRecord | null> => {
  await dbStorage.ready()
  const record = await dbStorage.get('ledger_safe_active_ledgers', contextId) as LedgerSafeActiveLedgerRecord | undefined
  return record || null
}

export const getLedgerSafeLedgerState = async (
  stateId: string,
): Promise<LedgerSafeLedgerStateRecord | null> => {
  await dbStorage.ready()
  const record = await dbStorage.get('ledger_safe_ledger_states', stateId) as LedgerSafeLedgerStateRecord | undefined
  return record || null
}

export const getLedgerSafeLedger = async (
  ledgerId: string,
): Promise<LedgerSafeLedgerRecord | null> => {
  await dbStorage.ready()
  const record = await dbStorage.get('ledger_safe_ledgers', ledgerId) as LedgerSafeLedgerRecord | undefined
  return record || null
}

export const renameLedgerSafeLedger = async (
  ledgerId: string,
  name: string,
): Promise<LedgerSafeLedgerRecord> => {
  const trimmedName = name.trim()
  if (!trimmedName) {
    throw new Error('Ledger name cannot be empty.')
  }
  const ledger = await getLedgerSafeLedger(ledgerId)
  if (!ledger) {
    throw new Error('Ledger not found.')
  }
  if (ledger.name === trimmedName) {
    return ledger
  }
  const updatedLedger: LedgerSafeLedgerRecord = {
    ...ledger,
    name: trimmedName,
    updatedAt: nowIso(),
  }
  await dbStorage.put('ledger_safe_ledgers', updatedLedger)
  return updatedLedger
}

export const setLedgerSafeActiveState = async (
  contextId: string,
  state: LedgerSafeLedgerStateRecord,
): Promise<LedgerSafeActiveLedgerRecord> => {
  const timestamp = nowIso()
  const active: LedgerSafeActiveLedgerRecord = {
    contextId,
    ledgerId: state.ledgerId,
    stateId: state.id,
    stateKind: state.kind,
    activatedAt: timestamp,
    updatedAt: timestamp,
  }
  await dbStorage.put('ledger_safe_active_ledgers', active)
  if (state.kind === 'working') {
    const ledger = await dbStorage.get('ledger_safe_ledgers', state.ledgerId) as LedgerSafeLedgerRecord | undefined
    if (ledger) {
      await dbStorage.put('ledger_safe_ledgers', {
        ...ledger,
        updatedAt: timestamp,
        latestWorkingStateId: state.id,
      })
    }
  }
  return active
}

export const createLedgerSafeLedger = async ({
  contextId,
  name,
  source,
  rows,
  witnessStates,
  activate = true,
}: {
  contextId: string
  name: string
  source: LedgerSafeLedgerSource
  rows: AnchoredRow[]
  witnessStates: LedgerSafeWitnessStateSnapshot[]
  activate?: boolean
}): Promise<LedgerSafeLedgerBundle> => {
  await dbStorage.ready()
  const timestamp = nowIso()
  const ledgerId = generateId('ledger-safe-ledger')
  const baselineStateId = generateId('ledger-safe-baseline')
  const workingStateId = generateId('ledger-safe-working')

  const baseline: LedgerSafeLedgerStateRecord = {
    id: baselineStateId,
    ledgerId,
    contextId,
    kind: 'baseline',
    label: 'Clean baseline',
    createdAt: timestamp,
    updatedAt: timestamp,
    rowCount: rows.length,
    rows: cloneRows(rows),
    witnessStates: cloneWitnessStates(witnessStates),
  }
  const working: LedgerSafeLedgerStateRecord = {
    id: workingStateId,
    ledgerId,
    contextId,
    kind: 'working',
    label: 'Edited state',
    createdAt: timestamp,
    updatedAt: timestamp,
    parentStateId: baselineStateId,
    rowCount: rows.length,
    rows: cloneRows(rows),
    witnessStates: cloneWitnessStates(witnessStates),
  }
  const ledger: LedgerSafeLedgerRecord = {
    id: ledgerId,
    contextId,
    name,
    createdAt: timestamp,
    updatedAt: timestamp,
    baselineStateId,
    latestWorkingStateId: workingStateId,
    source,
  }

  await dbStorage.put('ledger_safe_ledgers', ledger)
  await dbStorage.put('ledger_safe_ledger_states', baseline)
  await dbStorage.put('ledger_safe_ledger_states', working)
  if (activate) {
    await setLedgerSafeActiveState(contextId, working)
  }

  return {
    ledger,
    states: [working, baseline],
    baseline,
    latestWorking: working,
  }
}

export const createLedgerSafeWorkingStateFromBaseline = async ({
  ledger,
  baseline,
  label = 'Working copy from baseline',
  activate = true,
}: {
  ledger: LedgerSafeLedgerRecord
  baseline: LedgerSafeLedgerStateRecord
  label?: string
  activate?: boolean
}): Promise<LedgerSafeLedgerStateRecord> => {
  const timestamp = nowIso()
  const working: LedgerSafeLedgerStateRecord = {
    id: generateId('ledger-safe-working'),
    ledgerId: ledger.id,
    contextId: ledger.contextId,
    kind: 'working',
    label,
    createdAt: timestamp,
    updatedAt: timestamp,
    parentStateId: baseline.id,
    rowCount: baseline.rows.length,
    rows: cloneRows(baseline.rows),
    witnessStates: cloneWitnessStates(baseline.witnessStates),
  }
  await dbStorage.put('ledger_safe_ledger_states', working)
  await dbStorage.put('ledger_safe_ledgers', {
    ...ledger,
    updatedAt: timestamp,
    latestWorkingStateId: working.id,
  })
  if (activate) {
    await setLedgerSafeActiveState(ledger.contextId, working)
  }
  return working
}

export const createLedgerSafeLedgerFromStates = async ({
  contextId,
  name,
  source,
  baselineRows,
  baselineWitnessStates,
  workingRows,
  workingWitnessStates,
  workingLabel = 'Recovered edited state',
  activateStateKind = 'working',
}: {
  contextId: string
  name: string
  source: LedgerSafeLedgerSource
  baselineRows: AnchoredRow[]
  baselineWitnessStates: LedgerSafeWitnessStateSnapshot[]
  workingRows?: AnchoredRow[] | null
  workingWitnessStates?: LedgerSafeWitnessStateSnapshot[] | null
  workingLabel?: string
  activateStateKind?: 'baseline' | 'working'
}): Promise<LedgerSafeLedgerBundle> => {
  await dbStorage.ready()
  const timestamp = nowIso()
  const ledgerId = generateId('ledger-safe-ledger')
  const baselineStateId = generateId('ledger-safe-baseline')
  const workingStateId = workingRows ? generateId('ledger-safe-working') : undefined

  const baseline: LedgerSafeLedgerStateRecord = {
    id: baselineStateId,
    ledgerId,
    contextId,
    kind: 'baseline',
    label: 'Clean baseline',
    createdAt: timestamp,
    updatedAt: timestamp,
    rowCount: baselineRows.length,
    rows: cloneRows(baselineRows),
    witnessStates: cloneWitnessStates(baselineWitnessStates),
  }
  const latestWorking: LedgerSafeLedgerStateRecord | null = workingRows && workingStateId
    ? {
        id: workingStateId,
        ledgerId,
        contextId,
        kind: 'working',
        label: workingLabel,
        createdAt: timestamp,
        updatedAt: timestamp,
        parentStateId: baselineStateId,
        rowCount: workingRows.length,
        rows: cloneRows(workingRows),
        witnessStates: cloneWitnessStates(workingWitnessStates || baselineWitnessStates),
      }
    : null
  const ledger: LedgerSafeLedgerRecord = {
    id: ledgerId,
    contextId,
    name,
    createdAt: timestamp,
    updatedAt: timestamp,
    baselineStateId,
    ...(latestWorking ? { latestWorkingStateId: latestWorking.id } : {}),
    source,
  }

  await dbStorage.put('ledger_safe_ledgers', ledger)
  await dbStorage.put('ledger_safe_ledger_states', baseline)
  if (latestWorking) {
    await dbStorage.put('ledger_safe_ledger_states', latestWorking)
  }
  const activeState = activateStateKind === 'baseline' || !latestWorking ? baseline : latestWorking
  await setLedgerSafeActiveState(contextId, activeState)

  return {
    ledger,
    states: latestWorking ? [latestWorking, baseline] : [baseline],
    baseline,
    latestWorking,
  }
}

export const saveActiveLedgerSafeStateFromRows = async ({
  contextId,
  rows,
  witnesses,
}: {
  contextId: string
  rows: AnchoredRow[]
  witnesses: Witness[]
}): Promise<LedgerSafeLedgerStateRecord | null> => {
  const active = await getLedgerSafeActiveLedger(contextId)
  if (!active) {
    return null
  }
  const state = await getLedgerSafeLedgerState(active.stateId)
  const ledger = await getLedgerSafeLedger(active.ledgerId)
  if (!state || !ledger) {
    return null
  }
  const witnessStates = buildLedgerSafeWitnessStateSnapshots(rows, witnesses)
  const timestamp = nowIso()

  if (state.kind === 'baseline') {
    if (buildLedgerSafeStateHash(rows, witnessStates) === buildLedgerSafeStateHash(state.rows, state.witnessStates)) {
      return state
    }
    const working: LedgerSafeLedgerStateRecord = {
      id: generateId('ledger-safe-working'),
      ledgerId: ledger.id,
      contextId,
      kind: 'working',
      label: 'Edited state from clean baseline',
      createdAt: timestamp,
      updatedAt: timestamp,
      parentStateId: state.id,
      rowCount: rows.length,
      rows: cloneRows(rows),
      witnessStates: cloneWitnessStates(witnessStates),
    }
    await dbStorage.put('ledger_safe_ledger_states', working)
    await dbStorage.put('ledger_safe_ledgers', {
      ...ledger,
      updatedAt: timestamp,
      latestWorkingStateId: working.id,
    })
    await setLedgerSafeActiveState(contextId, working)
    return working
  }

  const updatedState: LedgerSafeLedgerStateRecord = {
    ...state,
    updatedAt: timestamp,
    rowCount: rows.length,
    rows: cloneRows(rows),
    witnessStates: cloneWitnessStates(witnessStates),
  }
  await dbStorage.put('ledger_safe_ledger_states', updatedState)
  await dbStorage.put('ledger_safe_ledgers', {
    ...ledger,
    updatedAt: timestamp,
    latestWorkingStateId: updatedState.id,
  })
  return updatedState
}

export const ensureLedgerSafeActiveLedgerForRows = async ({
  contextId,
  rows,
  witnesses,
  name = 'Legacy active collation version',
}: {
  contextId: string
  rows: AnchoredRow[]
  witnesses: Witness[]
  name?: string
}): Promise<LedgerSafeLedgerBundle | null> => {
  const active = await getLedgerSafeActiveLedger(contextId)
  if (active || rows.length === 0) {
    return null
  }
  const witnessStates = buildLedgerSafeWitnessStateSnapshots(rows, witnesses)
  return createLedgerSafeLedger({
    contextId,
    name,
    source: {
      type: 'legacy-active-ledger',
      representativeWitnessSigla: witnessStates.map(snapshot => snapshot.sigla),
      selectedWitnessSigla: witnessStates.map(snapshot => snapshot.sigla),
      summary: {
        row_count: rows.length,
        witness_count: new Set(rows.flatMap(row => row.unitMappings.map(mapping => mapping.witnessId))).size,
      },
    },
    rows,
    witnessStates,
    activate: true,
  })
}
